import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ClarityModule } from '@clr/angular';
import { finalize } from 'rxjs';
import { environment } from '../../../../../environments/environment';

/**
 * Frontend #3 — post-broadcast correction-export review.
 *
 * Loads the unified segment export for a broadcast (served live from the
 * voxtral-server while the EC2 is up, from the S3 mirror once it is gone) and
 * renders a filterable, expandable list — per-segment text, corrected_text,
 * per-model Part-B corrections[] rows and the growing partial history (Topic C).
 *
 * Lazy-loaded behind an expand toggle in details.component so opening a
 * broadcast dialog doesn't fire an export GET that may be hundreds of KB.
 */
@Component({
  selector: 'app-voxtral-export-review',
  standalone: true,
  imports: [CommonModule, FormsModule, ClarityModule],
  templateUrl: './voxtral-export-review.component.html',
  styleUrl: './voxtral-export-review.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VoxtralExportReviewComponent implements OnChanges {
  @Input() broadcastKey!: string;

  loading = false;
  errorMessage = '';
  export: any = null;

  // Filter / view controls.
  scope: 'all' | 'final' | 'partial' = 'final';
  onlyWithCorrections = false;
  outcomeFilter: 'all' | 'accepted' | 'rejected_or_abstain' = 'all';
  searchText = '';

  // Pagination — virtualised lists are out of scope; a coarse slice keeps
  // the DOM manageable for typical 200-1500-segment exports.
  pageSize = 200;
  pageIndex = 0;

  // Per-row expansion state, keyed by segment seq.
  private _expanded = new Set<number>();

  constructor(private http: HttpClient) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['broadcastKey'] && this.broadcastKey) {
      this.refresh();
    }
  }

  refresh(): void {
    if (!this.broadcastKey) return;
    this.loading = true;
    this.errorMessage = '';
    const url = `${environment.API_Base}/broadcast/voxtral-segments-export?broadcastKey=${encodeURIComponent(this.broadcastKey)}`;
    this.http.get<any>(url).pipe(finalize(() => this.loading = false)).subscribe({
      next: (resp) => { this.export = resp || {}; this.pageIndex = 0; this._expanded.clear(); },
      error: (err) => {
        this.errorMessage = err?.error?.message || err?.message || 'Export konnte nicht geladen werden.';
        this.export = null;
      },
    });
  }

  isExpanded(seq: number): boolean { return this._expanded.has(seq); }
  toggle(seq: number): void {
    if (this._expanded.has(seq)) this._expanded.delete(seq); else this._expanded.add(seq);
  }

  /** Apply filters + pagination to the loaded export. Cheap; recomputed by OnPush. */
  get filteredSegments(): any[] {
    const segs: any[] = (this.export?.segments) || [];
    const needle = (this.searchText || '').trim().toLowerCase();
    const filtered = segs.filter((s) => {
      if (this.scope !== 'all' && s.scope !== this.scope) return false;
      const corrections = (s.corrections || []) as any[];
      if (this.onlyWithCorrections && corrections.length === 0) return false;
      if (this.outcomeFilter !== 'all') {
        const accepted = corrections.some((c) => c.accepted);
        const rejected = corrections.some((c) => !c.accepted);
        if (this.outcomeFilter === 'accepted' && !accepted) return false;
        if (this.outcomeFilter === 'rejected_or_abstain' && !rejected) return false;
      }
      if (needle) {
        const hay = ((s.text || '') + ' ' + (s.corrected_text || '')).toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    // Pagination slice.
    const start = this.pageIndex * this.pageSize;
    return filtered.slice(start, start + this.pageSize);
  }

  get totalAfterFilter(): number {
    // O(n) — kept simple; for very large exports the page slice above is the
    // expensive part, this just counts.
    const segs: any[] = (this.export?.segments) || [];
    const needle = (this.searchText || '').trim().toLowerCase();
    return segs.filter((s) => {
      if (this.scope !== 'all' && s.scope !== this.scope) return false;
      if (needle) {
        const hay = ((s.text || '') + ' ' + (s.corrected_text || '')).toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    }).length;
  }

  pageCount(): number {
    return Math.max(1, Math.ceil(this.totalAfterFilter / this.pageSize));
  }

  nextPage(): void { if (this.pageIndex + 1 < this.pageCount()) this.pageIndex++; }
  prevPage(): void { if (this.pageIndex > 0) this.pageIndex--; }

  /** Quick badge class for a corrections[] row's outcome. */
  badgeClass(c: any): string {
    if (c.accepted) return 'badge-accepted';
    const reason = (c.reason || '').toLowerCase();
    if (reason.includes('same as original') || reason.includes('not picked')) return 'badge-info';
    return 'badge-rejected';
  }

  trackBySeq(_i: number, s: any): number { return s?.seq ?? _i; }
  trackByModel(_i: number, c: any): string { return (c?.model_id || '') + '|' + _i; }
}
